/** Shared display formatters. Locale stays `undefined` (viewer's locale decides
 *  rendering); these set only the shape. */

import { thaiDay } from '@/lib/timezone';

// Date-only strings (YYYY-MM-DD) are parsed as LOCAL calendar dates so the day
// never shifts when the viewer is behind/ahead of UTC. Full ISO timestamps parse
// normally. Returns null for unparseable input.
function toDate(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * The Thai calendar date an instant falls on, `YYYY-MM-DD`, or the value unchanged if it already
 * is one. A date the school picked as a *day* but the API stores as a moment — a deadline is the
 * end of its day — must read as that same day for every viewer instead of shifting with the
 * browser's own zone, and a date picker needs the plain date back.
 */
export function toThaiDateIso(value: string | null | undefined): string | null {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return thaiDay(d);
}

/** `timeZone` pins an instant to a zone (the school's, say) instead of the viewer's. */
export function formatDate(value: string | null | undefined, timeZone?: string): string {
  if (!value) return '—';
  const d = toDate(value);
  return d ? d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone }) : value;
}

/** Same as `formatDate`, spelling out the month — for a deadline mentioned on its own, where the
 *  abbreviation reads as too casual for a date a teacher is being held to. */
export function formatDateLong(value: string | null | undefined): string {
  if (!value) return '—';
  const d = toDate(value);
  return d ? d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }) : value;
}

/** A single day when both ends match, an arrow-joined range otherwise. */
export function formatDateRange(start: string | null | undefined, end: string | null | undefined): string {
  if (start === end) return formatDate(start);
  return `${formatDate(start)} → ${formatDate(end)}`;
}

/** Day + month, no year — for a short inline mention (e.g. "already serving until 2 Oct")
 *  where the year is noise the reader doesn't need. */
export function formatDateShort(value: string | null | undefined, timeZone?: string): string {
  if (!value) return '—';
  const d = toDate(value);
  return d ? d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone }) : value;
}

export function formatDateTime(value: string | null | undefined, timeZone?: string): string {
  if (!value) return '—';
  const d = toDate(value);
  return d
    ? d.toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone,
      })
    : value;
}

export function formatTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = toDate(value);
  return d ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false }) : value;
}

/** Bare 24h clock strings (e.g. "9:15", "09:15" or the API's "09:15:00"), NOT ISO/date strings —
 *  use `formatTime` for those. Normalizes to zero-padded "HH:MM". */
export function formatClockTime(value: string | null | undefined): string {
  if (!value) return '—';
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value);
  if (!m) return value;
  return `${m[1].padStart(2, '0')}:${m[2]}`;
}

/** A whole percentage, or a dash when there is nothing to measure. */
export function formatPercent(value: number | null | undefined): string {
  return value == null ? '—' : `${value}%`;
}

export function formatNumber(value: number | null | undefined): string {
  if (value == null) return '—';
  return value.toLocaleString(undefined);
}

/** Whole seconds as `m:ss` (e.g. 222 → "3:42"). Not locale-sensitive — a clock reads the same
 *  shape everywhere; only the digits (rendered via toLocaleString elsewhere) vary by locale. */
export function formatDuration(totalSeconds: number | null | undefined): string {
  if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds < 0) return '—';
  const rounded = Math.round(totalSeconds);
  const minutes = Math.floor(rounded / 60);
  const seconds = rounded % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

// Locale stays `undefined` here too, same as the rest of this file — the viewer's
// locale decides wording ("2 hours ago" vs. its translated equivalent). Returns null
// for unparseable/missing input so callers can substitute their own "Never"/"—" copy.
const RELATIVE_TIME_DIVISIONS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 60 * 60 * 24 * 365],
  ['month', 60 * 60 * 24 * 30],
  ['week', 60 * 60 * 24 * 7],
  ['day', 60 * 60 * 24],
  ['hour', 60 * 60],
  ['minute', 60],
];

export function formatRelativeTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const diffSeconds = Math.round((d.getTime() - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  for (const [unit, secondsInUnit] of RELATIVE_TIME_DIVISIONS) {
    if (Math.abs(diffSeconds) >= secondsInUnit) {
      return rtf.format(Math.round(diffSeconds / secondsInUnit), unit);
    }
  }
  return rtf.format(diffSeconds, 'second');
}

/**
 * A phone number grouped the way people read it aloud.
 *
 * Thai numbers get real grouping — mobiles as +66 81 667 4003, landlines as +66 2 123 4567,
 * and the local 0-prefixed form as 081 667 4003. Anything else is returned as typed: guessing
 * at a grouping for a country whose conventions we do not know reads worse than leaving it be.
 */
export function formatPhone(value: string | null | undefined): string {
  const raw = value?.trim();
  if (!raw) return '';

  const digits = raw.replace(/\D/g, '');
  const group = (...parts: string[]) => parts.join(' ');

  if (raw.startsWith('+66') || digits.startsWith('66')) {
    const national = digits.slice(2);
    // Mobiles are 9 digits after the country code (81 667 4003), landlines 8 (2 123 4567).
    if (national.length === 9) return `+66 ${group(national.slice(0, 2), national.slice(2, 5), national.slice(5))}`;
    if (national.length === 8) return `+66 ${group(national.slice(0, 1), national.slice(1, 4), national.slice(4))}`;
    return raw;
  }

  if (!raw.startsWith('+') && digits.length === 10 && digits.startsWith('0')) {
    return group(digits.slice(0, 3), digits.slice(3, 6), digits.slice(6));
  }

  return raw;
}
